import {
  Component, ContentChild, ContentChildren, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList,
  TemplateRef
} from "@angular/core";
import {DxPageHeaderOptions} from "../../../domain/ui-options/page-header/dx-page-header-options";
import {DxPageHeaderAppLogoDirective} from "../../directives/page-header/dx-page-header-app-logo.directive";
import {DxPageHeaderAppTitleDirective} from "../../directives/page-header/dx-page-header-app-title.directive";
import {DxPageHeaderNavBarDirective} from "../../directives/page-header/dx-page-header-nav-bar.directive";
import {DxPageHeaderLogoutBarDirective} from "../../directives/page-header/dx-page-header-logout-bar.directive";
import {DxPageHeaderUserBarDirective} from "../../directives/page-header/dx-page-header-user-bar.directive";
import {DxPageHeaderHelpBarDirective} from "../../directives/page-header/dx-page-header-help-bar.directive";
import {DxPageHeaderNotificationBarDirective} from "../../directives/page-header/dx-page-header-notification-bar.directive";
import {DxPageHeaderSettingsBarDirective} from "../../directives/page-header/dx-page-header-settings-bar.directive";
import {DxPageHeaderComboDirective} from "../../directives/page-header/dx-page-header-combo.directive";
import {DxPageHeaderItemOptions} from "../../../domain/ui-options/page-header/dx-page-header-item-options";
import {DxPageHeaderToolBtnDirective} from "../../directives/page-header/dx-page-header-tool-btn.directive";
import {DxPageHeaderItemContext} from "../../directives/page-header/dx-page-header.interfaces";
import {DxPageHeaderGroupOptions} from "../../../domain/ui-options/page-header/dx-page-header-group-options";
import {DxPageHeaderItemType} from "../../../domain/ui-options/page-header/dx-page-header-item-type";
import {DxAppEvent} from "../../../../dx-core/domain/events/dx-app-event";


/**
 * Шаблонизированный компонент для шапки страницы.
 * Компонент несет в себе предопределенную струкетуру шапки. Шапка состоит из частей (групп) элементов.
 * Каждая группа представляет собой последовательность элементов, разделенных\не разделенных элементом-сепаратором.
 * Элементы заголовка поделены на типы. Кнопки и комбобоксы могут присутствовать во множественном числе, отстальные
 * только в единичном экземпляре.
 *
 * Типы элементов шапки описаны в енуме `DxPageHeaderItemType`.
 *
 * Каждый элемен шапки может быть при помощи структрных директив заменен на кастомный шаблон.
 * Реализация дефолтных шаблонов элементов шапки тривиальна. Просто `<div></div>` с минимальным оформлением куда выводится текст.
 *
 * Конфигурация шапки задается при помощи объекта класса `DxPageHeaderOptions`.
 *
 * @publicApi
 */
@Component({
  selector: 'dx-page-header',
  templateUrl: './dx-page-header.component.html',
  styleUrls: ['./dx-page-header.component.css']
})
export class DxPageHeaderComponent implements OnInit, OnDestroy {
  /**
   * Настройки шапки страницы.
   * @name options
   * @type {DxPageHeaderOptions}
   */
  @Input()
  public options: DxPageHeaderOptions;

  /**
   * Шаблон области логотипа приложения, определенный вне компонента при помощи структурной директивы.
   * @name pageHeaderAppLogoTemplate
   * @type {DxPageHeaderAppLogoDirective}
   */
  @ContentChild(DxPageHeaderAppLogoDirective, {static: true, read: TemplateRef})
  public pageHeaderAppLogoTemplate: DxPageHeaderAppLogoDirective;

  /**
   * Шаблон области названия приложения, определенный вне компонента при помощи структурной директивы.
   * @name pageHeaderAppTitleTemplate
   * @type {DxPageHeaderAppTitleDirective}
   */
  @ContentChild(DxPageHeaderAppTitleDirective, {static: true, read: TemplateRef})
  public pageHeaderAppTitleTemplate: DxPageHeaderAppTitleDirective;

  /**
   * Шаблон области навигации в шапке, определенный вне компонента при помощи структурной директивы.
   * @name pageHeaderNavBarTemplate
   * @type {DxPageHeaderNavBarDirective}
   */
  @ContentChild(DxPageHeaderNavBarDirective, {static: true, read: TemplateRef})
  public pageHeaderNavBarTemplate: DxPageHeaderNavBarDirective;

  /**
   * Шаблон области под раздел помощи\справки, определенный вне компонента при помощи структурной директивы.
   * @name pageHeaderHelpBarTemplate
   * @type {DxPageHeaderHelpBarDirective}
   */
  @ContentChild(DxPageHeaderHelpBarDirective, {static: true, read: TemplateRef})
  public pageHeaderHelpBarTemplate: DxPageHeaderHelpBarDirective;

  /**
   * Шаблон области под раздел уведомлений, определенный вне компонента при помощи структурной директивы.
   * @name pageHeaderNotificationBarTemplate
   * @type {DxPageHeaderNotificationBarDirective}
   */
  @ContentChild(DxPageHeaderNotificationBarDirective, {static: true, read: TemplateRef})
  public pageHeaderNotificationBarTemplate: DxPageHeaderNotificationBarDirective;

  /**
   * Шаблон области под раздел настроек, определенный вне компонента при помощи структурной директивы.
   * @name pageHeaderSettingsBarTemplate
   * @type {DxPageHeaderSettingsBarDirective}
   */
  @ContentChild(DxPageHeaderSettingsBarDirective, {static: true, read: TemplateRef})
  public pageHeaderSettingsBarTemplate: DxPageHeaderSettingsBarDirective;

  /**
   * Шаблон области под область с информацией о текущем пользователе, определенный вне компонента при помощи структурной директивы.
   * @name pageHeaderUserBarTemplate
   * @type {DxPageHeaderUserBarDirective}
   */
  @ContentChild(DxPageHeaderUserBarDirective, {static: true, read: TemplateRef})
  public pageHeaderUserBarTemplate: DxPageHeaderUserBarDirective;

  /**
   * Шаблон области под область с кнопкой логаута или иного механизма выхода из системы, определенный вне компонента при помощи структурной директивы.
   * @name pageHeaderLogoutBarTemplate
   * @type {DxPageHeaderLogoutBarDirective}
   */
  @ContentChild(DxPageHeaderLogoutBarDirective, {static: true, read: TemplateRef})
  public pageHeaderLogoutBarTemplate: DxPageHeaderLogoutBarDirective;

  /**
   * Список шаблон комбобоксов, определенных вне компонента при помощи структурной директивы.
   * @name pageHeaderComboTemplates
   * @type {QueryList<DxPageHeaderComboDirective>}
   */
  @ContentChildren(DxPageHeaderComboDirective)
  public pageHeaderComboTemplates: QueryList<DxPageHeaderComboDirective>;

  /**
   * Список шаблон конопок, определенных вне компонента при помощи структурной директивы.
   * @name pageHeaderToolBtnTemplates
   * @type {QueryList<DxPageHeaderToolBtnDirective>}
   */
  @ContentChildren(DxPageHeaderToolBtnDirective)
  public pageHeaderToolBtnTemplates: QueryList<DxPageHeaderToolBtnDirective>;

  /**
   * Эммиттер собития клика по логотипу приложения в шапке.
   * @name appLogoClick
   * @type {EventEmitter<DxAppEvent>}
   */
  @Output()
  public appLogoClick: EventEmitter<DxAppEvent> = new EventEmitter();

  constructor() {}


  ngOnInit(): void {
    if(!this.options) {
      this.options = this._initDefaults();
    }
  }

  ngOnDestroy(): void { }

  /** Обрабатывает клик по логотипу, вызывая выброс события через эмиттер `appLogoClick`. */
  public onAppLogoClick(): void {
    this.appLogoClick.emit(new DxAppEvent('app-logo'));
  }

  /**
   * Находит шаблон комбобокса для элемента заголовка. Сопоставление происходит по идентам.
   * @param {DxPageHeaderItemOptions} item - объект опций элемента шапки.
   * @return {TemplateRef<any>} - ссылка на шаблон.
   */
  public findHeaderComboTemplate(item: DxPageHeaderItemOptions): TemplateRef<any> {
    if(this.pageHeaderComboTemplates) return null;
    const directive = this.pageHeaderComboTemplates.find((template) => template.ident === item.ident);
    if(directive) {
      return directive.template;
    }
    return null;
  }


  /**
   * Находит шаблон кнопки для элемента заголовка. Сопоставление происходит по идентам.
   * @param {DxPageHeaderItemOptions} item - объект опций элемента шапки.
   * @return {TemplateRef<any>} - ссылка на шаблон.
   */
  public findHeaderToolBtnTemplate(item: DxPageHeaderItemOptions): TemplateRef<any> {
    if(this.pageHeaderToolBtnTemplates) return null;
    const directive = this.pageHeaderToolBtnTemplates.find((template) => template.ident === item.ident);
    if(directive) {
      return directive.template;
    }
    return null;
  }

  /**
   * Создает контекст шаблона элемента шапки страницы.
   * @param {DxPageHeaderItemOptions} item - объект с опциями элемента шапки страницы.
   * @return {DxPageHeaderItemContext} - контекст шаблона элемента шапки страницы.
   */
  public buildHeaderItemContext(item: DxPageHeaderItemOptions): DxPageHeaderItemContext {
    return {
      $implicit: item
    };
  }

  /** Инициализирует дефолтную конфигурацию шапки страницы. */
  private _initDefaults(): DxPageHeaderOptions {
    const options: DxPageHeaderOptions = new DxPageHeaderOptions();
    options.groups = [];

    let group: DxPageHeaderGroupOptions = new DxPageHeaderGroupOptions();
    group.items = [];
    group.ord = 1;

    let item: DxPageHeaderItemOptions = new DxPageHeaderItemOptions();
    item.ident = 'DX_APP_LOGO';
    item.type = DxPageHeaderItemType.APP_LOGO.toString();
    item.title = 'DX APP LOGO';
    item.ord = 1;
    item.iconPath = 'assets/img/dx-img/dx-core/dx-default-app-logo.svg';
    item.addSeparatorAfter = false;
    group.items.push(item);

    item = new DxPageHeaderItemOptions();
    item.ident = 'DX_APP_NAME';
    item.type = DxPageHeaderItemType.APP_NAME.toString();
    item.title = 'DX APP NAME';
    item.ord = 2;
    item.multiLineTitle = ['Дефолтная информационно-аналитическая система', 'Данный блок требует переопределения в прикланом приложении'];
    item.addSeparatorAfter = false;
    group.items.push(item);

    options.groups.push(group);

    group = new DxPageHeaderGroupOptions();
    group.items = [];
    group.ord = 2;

    item = new DxPageHeaderItemOptions();
    item.ident = 'DX_NAVIGATION_BAR';
    item.type = DxPageHeaderItemType.NAVIGATION_BAR.toString();
    item.title = 'DX NAVIGATION BAR';
    item.ord = 1;
    item.addSeparatorAfter = false;
    group.items.push(item);

    options.groups.push(group);


    group = new DxPageHeaderGroupOptions();
    group.items = [];
    group.ord = 3;

    item = new DxPageHeaderItemOptions();
    item.ident = 'DX_HEADER_COMBO_1';
    item.type = DxPageHeaderItemType.COMBO.toString();
    item.title = 'DX HEADER COMBO 1';
    item.ord = 1;
    item.addSeparatorAfter = true;
    group.items.push(item);

    item = new DxPageHeaderItemOptions();
    item.ident = 'DX_HEADER_COMBO_2';
    item.type = DxPageHeaderItemType.COMBO.toString();
    item.title = 'DX HEADER COMBO 2';
    item.ord = 2;
    item.addSeparatorAfter = false;
    group.items.push(item);

    options.groups.push(group);


    group = new DxPageHeaderGroupOptions();
    group.items = [];
    group.ord = 4;

    item = new DxPageHeaderItemOptions();
    item.ident = 'DX_NOTIFICATION_BAR';
    item.type = DxPageHeaderItemType.NOTIFICATION_BAR.toString();
    item.title = 'DX NOTIFICATION BAR';
    item.ord = 1;
    item.addSeparatorAfter = true;
    group.items.push(item);

    item = new DxPageHeaderItemOptions();
    item.ident = 'DX_SETTINGS_BAR';
    item.type = DxPageHeaderItemType.SETTINGS_BAR.toString();
    item.title = 'DX SETTINGS BAR';
    item.ord = 2;
    item.addSeparatorAfter = true;
    group.items.push(item);

    item = new DxPageHeaderItemOptions();
    item.ident = 'DX_HELP_BAR';
    item.type = DxPageHeaderItemType.HELP_BAR.toString();
    item.title = 'DX HELP BAR';
    item.ord = 3;
    item.addSeparatorAfter = true;
    group.items.push(item);

    item = new DxPageHeaderItemOptions();
    item.ident = 'DX_USER_BAR';
    item.type = DxPageHeaderItemType.USER_BAR.toString();
    item.title = 'DX USER BAR';
    item.ord = 4;
    item.addSeparatorAfter = true;
    group.items.push(item);

    item = new DxPageHeaderItemOptions();
    item.ident = 'DX_LOGOUT_BAR';
    item.type = DxPageHeaderItemType.LOGOUT_BAR.toString();
    item.title = 'DX LOGOUT BAR';
    item.ord = 5;
    item.addSeparatorAfter = false;
    group.items.push(item);

    options.groups.push(group);

    return options;
  }
}
